// Session persistence utility for maintaining state across page reloads
import { useState, useEffect } from 'react';

export const SessionKeys = {
  LAST_VISITED_PAGE: 'lastVisitedPage',
  INVENTORY_TAB: 'inventoryTab',
  ADJUSTMENT_VIEW: 'adjustmentView',
  FILTER_PREFERENCES: 'filterPreferences',
  TABLE_PREFERENCES: 'tablePreferences'
};

export const sessionStorage = {
  get: (key, defaultValue = null) => {
    try {
      const stored = window.sessionStorage.getItem(key);
      return stored ? JSON.parse(stored) : defaultValue;
    } catch (error) {
      console.warn(`Failed to get session storage key ${key}:`, error);
      return defaultValue;
    }
  },

  set: (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.warn(`Failed to set session storage key ${key}:`, error);
    }
  },

  remove: (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove session storage key ${key}:`, error);
    }
  },

  clear: () => {
    try {
      window.sessionStorage.clear();
    } catch (error) {
      console.warn('Failed to clear session storage:', error);
    }
  }
};

export const useSessionState = (key, defaultValue) => {
  const [state, setState] = useState(() => {
    return sessionStorage.get(key, defaultValue);
  });

  useEffect(() => {
    sessionStorage.set(key, state);
  }, [key, state]);

  return [state, setState];
};

// Track last visited page for persistence
export const trackPageVisit = (pathname) => {
  sessionStorage.set(SessionKeys.LAST_VISITED_PAGE, {
    path: pathname,
    timestamp: Date.now()
  });
};

// Get last visited page if recent (within 1 hour)
export const getLastVisitedPage = () => {
  const lastVisit = sessionStorage.get(SessionKeys.LAST_VISITED_PAGE);
  if (!lastVisit) return null;
  
  const oneHour = 60 * 60 * 1000;
  const isRecent = (Date.now() - lastVisit.timestamp) < oneHour;
  
  return isRecent ? lastVisit.path : null;
};